/** Site institucional (marketing / SEO / planos informativos). */
export const MARKETING_SITE_ORIGIN = "https://constelabrasil.com.br";

/** Aplicação (login, checkout, painel, sala, agendamento). */
export const APP_SITE_ORIGIN = "https://app.constelabrasil.com.br";

const MARKETING_HOSTS = new Set(["constelabrasil.com.br", "www.constelabrasil.com.br"]);
const APP_HOSTS = new Set(["app.constelabrasil.com.br"]);

/** Rotas que existem apenas no app. */
export const APP_ROUTE_PREFIXES = [
  "/login",
  "/sair",
  "/esqueci-senha",
  "/redefinir-senha",
  "/__/auth/action",
  "/checkout",
  "/pagamento",
  "/agendamento",
  "/completar-perfil",
  "/painel",
  "/salax",
  "/sala",
  "/avaliar",
] as const;

/** Rotas do site institucional. */
export const MARKETING_ROUTE_PREFIXES = [
  "/home",
  "/constelador",
  "/sobre-nos",
  "/contato",
  "/tutoriais",
  "/planos",
  "/terapeutas",
  "/terapeuta",
  "/quero-participar",
  "/politica-de-privacidade",
  "/termos-de-servico",
] as const;

export function isLocalDevHost(hostname?: string): boolean {
  const h = hostname ?? (typeof window !== "undefined" ? window.location.hostname : "");
  return h === "localhost" || h === "127.0.0.1" || h.endsWith(".local");
}

export function isMarketingHost(hostname?: string): boolean {
  const h = hostname ?? (typeof window !== "undefined" ? window.location.hostname : "");
  return MARKETING_HOSTS.has(h);
}

export function isAppHost(hostname?: string): boolean {
  const h = hostname ?? (typeof window !== "undefined" ? window.location.hostname : "");
  return APP_HOSTS.has(h);
}

/** Domínio raiz já serve o React (não o Website Builder antigo). */
export function isMarketingDomainLive(): boolean {
  return process.env.REACT_APP_MARKETING_DOMAIN_LIVE !== "false";
}

/** Em produção, força marketing ↔ app; em localhost tudo fica no mesmo origin. */
export function shouldEnforceDomainSplit(): boolean {
  if (typeof window === "undefined") return false;
  if (isLocalDevHost()) return false;
  return isMarketingHost() || isAppHost();
}

function normalizePath(pathname: string): string {
  if (!pathname || pathname === "/") return "/";
  return pathname.replace(/\/+$/, "") || "/";
}

function matchesPrefix(pathname: string, prefix: string): boolean {
  const p = normalizePath(pathname);
  if (prefix.endsWith("/")) {
    return p.startsWith(prefix);
  }
  return p === prefix || p.startsWith(`${prefix}/`);
}

export function isAppRoute(pathname: string): boolean {
  return APP_ROUTE_PREFIXES.some((prefix) => matchesPrefix(pathname, prefix));
}

export function isMarketingRoute(pathname: string): boolean {
  const p = normalizePath(pathname);
  if (p === "/") return true;
  return MARKETING_ROUTE_PREFIXES.some((prefix) => matchesPrefix(pathname, prefix));
}

/** URL completa para redirect entre domínios (ou null se já estiver no host certo). */
export function getCrossDomainRedirectTarget(pathname: string, search = ""): string | null {
  if (!shouldEnforceDomainSplit()) return null;

  const query = search || "";

  if (isAppHost()) {
    if (!isMarketingDomainLive()) return null;
    if (normalizePath(pathname) === "/") {
      return `${MARKETING_SITE_ORIGIN}/${query ? query.replace(/^\?/, "?") : ""}`.replace("/?", "?");
    }
    if (isMarketingRoute(pathname)) {
      return `${MARKETING_SITE_ORIGIN}${pathname}${query}`;
    }
    return null;
  }

  if (isMarketingHost() && isAppRoute(pathname)) {
    return `${APP_SITE_ORIGIN}${pathname}${query}`;
  }

  return null;
}

export function toAppUrl(path: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  if (typeof window === "undefined") return `${APP_SITE_ORIGIN}${normalized}`;
  if (!shouldEnforceDomainSplit() || isAppHost()) return normalized;
  return `${APP_SITE_ORIGIN}${normalized}`;
}

export function toMarketingUrl(path: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  if (typeof window === "undefined") return `${MARKETING_SITE_ORIGIN}${normalized}`;
  if (!shouldEnforceDomainSplit() || isMarketingHost()) return normalized;
  return `${MARKETING_SITE_ORIGIN}${normalized}`;
}

/**
 * URL pública de indicação. Usa a landing no domínio principal com ?ref=.
 * Links em app.../r/:code quebram no WhatsApp (o crawler cola og:url no próprio link).
 */
export function affiliateTrackingUrl(code: string): string {
  const normalized = code.trim().toLowerCase();
  return `${MARKETING_SITE_ORIGIN}/constelador?ref=${encodeURIComponent(normalized)}`;
}
